import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        setInfo(res.data);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (tries >= MAX_TRIES) { setState("pending"); return; }
        tries += 1;
        setTimeout(poll, 2000);
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-navy text-white noise-overlay min-h-[70vh] flex items-center py-20">
      <div className="max-w-xl mx-auto px-4 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-bronze mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl font-bold mb-3">Confirming your payment…</h1>
            <p className="font-body text-gray-300">Please wait a moment while we verify with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h1 className="font-heading text-4xl font-bold mb-3">Payment Successful!</h1>
            <p className="font-body text-lg text-gray-300 mb-2">Thank you{info?.label ? ` — ${info.label}` : ""}.</p>
            {info?.amount != null && <p className="font-heading text-2xl text-bronze mb-6">${Number(info.amount).toFixed(2)} paid</p>}
            <p className="font-body text-sm text-gray-400 mb-8">A receipt has been emailed to you by Stripe. We'll follow up on your return.</p>
            <Button asChild className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-semibold"><Link to="/">Back to Home</Link></Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-14 h-14 text-bronze mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-3">Payment Processing</h1>
            <p className="font-body text-gray-300 mb-8">Your payment is still processing. If you were charged, you'll receive an email receipt shortly. Questions? Call 201-253-7674.</p>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 bg-transparent"><Link to="/">Back to Home</Link></Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-3">Could Not Verify Payment</h1>
            <p className="font-body text-gray-300 mb-8">We couldn't confirm this payment. If you were charged, please contact us at INFO@DATAXGUY.COM or 201-253-7674.</p>
            <Button asChild className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6"><Link to="/payments">Try Again</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
